import { useInvalidateReturnRequest } from "@babackapp/private-api-client/client/use-invalidate-return-request";
import useResendReturnRequestEmail from "@babackapp/private-api-client/client/use-resend-return-request-email";
import { ReturnRequestLogDto } from "@babackapp/private-api-client/types";
import useTranslation from "@babackapp/translations/use-translation";
import { Button } from "@babackapp/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@babackapp/ui/components/dialog";
import { Input } from "@babackapp/ui/components/input";
import { MailIcon, SendIcon } from "lucide-react";
import { useState } from "react";
import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import { useViewerIsSuperAdmin } from "@/lib/rights/use-viewer-is-baback-team";
import ActivityContentWrapper from "./activity-content-wrapper";
import ActivityIconWrapper from "./activity-icon-wrapper";
import { ActivityItemHeader } from "./activity-item-header";
import ActivityWrapper from "./activity-wrapper";

type EmailActivityProps = {
  activity: ReturnRequestLogDto;
  returnRequestId: string;
};

const RESENDABLE_EMAIL_TYPES = new Set([
  "newLabel",
  "newReturnRequest",
  "newReturnRequestWithoutPhysicalReturn",
  "depositReminder",
  "returnRequestValidated",
  "returnRequestRejected",
]);

// These types replay a lineItems snapshot captured at first send. Older log
// entries created before that snapshot existed have no params — resend still
// works via a degraded reconstruction from the return request's current
// state, but the result can differ from what was originally sent.
const SNAPSHOT_REQUIRED_EMAIL_TYPES = new Set([
  "returnRequestValidated",
  "returnRequestRejected",
]);

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Must match SUPERADMIN_RESEND_LOG_AUTHOR in return-requests-admin.controller.ts
const SUPERADMIN_RESEND_LOG_AUTHOR = "superadmin-resend";

export default function EmailActivity({
  activity,
  returnRequestId,
}: EmailActivityProps) {
  const t = useTranslation();
  const isSuperAdmin = useViewerIsSuperAdmin();
  const { data: shop } = useCurrentShopSuspense();
  const resendEmail = useResendReturnRequestEmail(shop?.id);
  const invalidateReturnRequest = useInvalidateReturnRequest();

  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("dev@baback.co");
  const [error, setError] = useState<string | null>(null);

  const emailNameByType = {
    newReturnRequest: t("returnDetail.requestCreation"),
    updateRequestStatus: t("returnDetail.requestUpdate"),
    newReturnRequestWithoutValidation: t(
      "returnDetail.newReturnRequestWithoutValidation",
    ),
    returnRequestValidated: t("returnDetail.returnRequestValidated"),
    discountCode: t("returnDetail.discountCode"),
    newReturnRequestWithoutLabel: t(
      "returnDetail.newReturnRequestWithoutLabel",
    ),
    newReturnRequestWithoutPhysicalReturn: t(
      "returnDetail.newReturnRequestWithoutPhysicalReturn",
    ),
    newReturnRequestReturnToStore: t(
      "returnDetail.newReturnRequestReturnToStore",
    ),
    newLabel: t("returnDetail.newLabel"),
    depositReminder: t("returnDetail.depositReminder"),
    returnRequestRejected: t("returnDetail.returnRequestRejected"),
  };

  // Workflow-sent emails carry the template identity in params — the label
  // is the template title (legacy types map to fixed labels below instead).
  const workflowTemplateTitle =
    activity.emailSent === "workflow"
      ? (activity.params as { templateTitle?: string } | undefined)
          ?.templateTitle || t("returnDetail.workflowEmail")
      : undefined;

  const hasSnapshot =
    !!activity.params && Object.keys(activity.params).length > 0;
  const canResend =
    isSuperAdmin &&
    !!activity.emailSent &&
    RESENDABLE_EMAIL_TYPES.has(activity.emailSent) &&
    activity.author !== SUPERADMIN_RESEND_LOG_AUTHOR;
  const isDegradedResend =
    !!activity.emailSent &&
    SNAPSHOT_REQUIRED_EMAIL_TYPES.has(activity.emailSent) &&
    !hasSnapshot;

  const isHiddenSuperAdminResend =
    activity.author === SUPERADMIN_RESEND_LOG_AUTHOR && !isSuperAdmin;

  const handleSubmit = async () => {
    if (!EMAIL_REGEX.test(email)) {
      setError(t("activities.email.resendInvalidEmail"));
      return;
    }
    setError(null);
    try {
      await resendEmail.mutateAsync({
        returnRequestId,
        logId: activity.id,
        email,
      });
      setOpen(false);
      setEmail("dev@baback.co");
      invalidateReturnRequest({ shopId: shop.id, returnRequestId });
    } catch {
      // Error toast is already surfaced by the mutation hook.
    }
  };

  if (isHiddenSuperAdminResend) {
    return null;
  }

  return (
    <ActivityWrapper>
      <ActivityIconWrapper>
        <MailIcon className="size-4" />
      </ActivityIconWrapper>
      <ActivityContentWrapper>
        <div className="flex items-center gap-2">
          <ActivityItemHeader
            title={t("activities.email.title")}
            timestamp={activity.date}
          >
            <span className="font-medium text-xs underline">
              {workflowTemplateTitle ||
                emailNameByType[
                  activity.emailSent as keyof typeof emailNameByType
                ] ||
                ""}
            </span>
          </ActivityItemHeader>
        </div>
        {activity.comment && (
          <p className="text-xs text-amber-700">{activity.comment}</p>
        )}
        {canResend && (
          <Dialog open={open} onOpenChange={setOpen}>
            <div className="mt-2 flex items-center justify-between gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
              <span className="text-xs text-amber-700 bg-amber-100 rounded px-2 py-0.5 font-medium">
                Baback CSM
              </span>
              <DialogTrigger asChild>
                <button
                  type="button"
                  className="flex items-center gap-1 rounded-md border border-amber-400 bg-white px-3 py-1.5 text-xs font-medium text-amber-800 hover:bg-amber-50 transition-colors"
                >
                  <SendIcon className="h-4 w-4" />
                  {t("activities.email.resend")}
                </button>
              </DialogTrigger>
            </div>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {t("activities.email.resendDialogTitle")}
                </DialogTitle>
              </DialogHeader>
              {isDegradedResend && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                  Aucun snapshot disponible pour cette entrée (créée avant
                  l'ajout de cette fonctionnalité). L'email sera reconstruit à
                  partir de l'état actuel du retour — le contenu (montants,
                  articles) peut différer de l'original.
                </p>
              )}
              <Input
                type="email"
                value={email}
                placeholder={t("activities.email.resendPlaceholder")}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (error) setError(null);
                }}
              />
              {error && <p className="text-sm text-red-600">{error}</p>}
              <DialogFooter>
                <Button onClick={handleSubmit} disabled={resendEmail.isPending}>
                  {t("activities.email.resendSubmit")}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </ActivityContentWrapper>
    </ActivityWrapper>
  );
}
